import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpService, ProjectDescription } from '../../../../core';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '../../../../core/services/user.service';
import { ProjectService } from '../../../../core/services/project.service';
import { Router } from '@angular/router';
import { AutocompleteComponent } from '../../../../shared/components/auto-complete/auto-complete.component';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Observable } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { debounceTime, finalize } from 'rxjs/operators';

/**
 * @author Damon Gao
 * @description compare max price and min price
 * @param group FormGroup
 */
export function comparePrice(group: FormGroup): ValidationErrors | null {
  const minPrice = group.get('minPrice').value;
  const maxPrice = group.get('maxPrice').value;

  if (minPrice == null || maxPrice == null || isNaN(minPrice) || isNaN(maxPrice)) {
    return null;
  }

  if (maxPrice === 0) {
    return null;
  }

  if (maxPrice >= minPrice) {
    return null;
  } else {
    return { priceMismatch: true };
  }

}

@Component({
  selector: 'app-basic-info',
  templateUrl: 'basic-info.component.html',
  styleUrls: ['./basic-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class BasicInfoComponent implements OnInit {
  @Input() isPackage: any;
  @Input() landProjectId: any;
  @Output() closeDialogEvent = new EventEmitter();
  selectedLat: any;
  selectedLng: any;
  geoCoder: any;
  tags = [];
  indoorFeatures = [];
  outdoorFeatures = [];
  energyFeatures = [];
  publicFeatures = [];
  selectedFeatureIdArray = [];
  companyId: any;
  reactiveForm: FormGroup;

  onlyNumberRegex = /^[0-9]*$/;
  isSubmitted: boolean;
  selectedUsers = [];
  selectedUserIds = [];
  ptypeShow = false;
  pstatusShow = false;
  pnameShow = false;
  projectType = [
    {
      name: 'House', value: 1
    }, {
      name: 'Townhouse', value: 2
    }, {
      name: 'Apartment', value: 3
    }, {
      name: 'House & Land', value: 4
    }
  ];

  priceType = [
    {
      name: 'any', value: 0
    }, {
      name: '1', value: 1
    }, {
      name: '2', value: 2
    }, {
      name: '3', value: 3
    }, {
      name: '4', value: 4
    }, {
      name: '5+', value: 5
    }
  ];
  projectStatus: any;
  selectedProjectType = [];
  isCreating: boolean;

  startTimestamp = '';
  endTimestamp = '';
  sunsetTimestamp = '';
  isGenerating = false;

  public addressLine1: any;
  public addressLine2: any;
  public suburb: any;
  public city: any;
  public state: any;
  public country: any;
  public postCode: any;
  public style = 'padding: 12px 10px; border: 0; width: calc(100% - 20px); outline: none; borderRadius: 4px;';
  private incentive = new Array(5);

  @ViewChild('addressForm', { static: false }) private addressForm: any;

  @ViewChild(AutocompleteComponent, { static: false })
  private autocomplete: AutocompleteComponent;
  history: string[] = [];
  debouncePeriod = 1000;
  lastEditTime = Date.now();

  constructor(
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private http: HttpService,
    private toastr: ToastrService,
    private ngZone: NgZone,
    private userService: UserService,
    private projectService: ProjectService,
    private dialog: MatDialog,
    private router: Router,
    private dialogRef: MatDialogRef<any>,
  ) {

    this.projectStatus = [
      // {
      //   name: 'Off market', value: 1
      // },
      {
        name: 'Off the plan', value: 2
      },
      {
        name: 'Under construction', value: 3
      }, {
        name: 'Complete', value: 4
      }
    ];

    this.reactiveForm = this.formBuilder.group({
      pname: ['', Validators.required],
      ptype: [''],
      website: [null],
      vrURLs: [null],
      videoURL: [null],
      totalUnit: [],
      level: [0],
      vrUrl: [null],
      introductionEN: ['', [Validators.maxLength(2000)]],
      introductionZH: ['', [Validators.maxLength(2000)]],
      commission: ['', [Validators.pattern(this.onlyNumberRegex)]],
      commissionUnit: ['%'], // max width: 20
      addressLine1: [''],
      // addressLine2: [''],
      area: [{ value: '', disabled: true }],
      city: [{ value: '', disabled: true }],
      state: [{ value: '', disabled: true }],
      postCode: [{ value: '', disabled: true }],
      country: [{ value: '', disabled: true }],
      status: [''],
      pstatus: [''],
      firb: [''],
      startDate: [''],
      endDate: [''],
      sunsetDate: [''],
      projectStatus: [''],
      incentive1: [''],
      incentive2: [''],
      incentive3: [''],
      incentive4: [''],
      incentive5: [''],
      tags: [''],
      cityCouncil: [''],
      lat: [''],
      lng: [''],
      maxPrice: ['', [Validators.min(-1)]],
      minPrice: ['', [Validators.min(-1)]],
      maxTotalArea: ['', [Validators.min(-1)]],
      minTotalArea: ['', [Validators.min(-1)]],
      maxBathRoom: [0, [Validators.min(-1)]],
      minBathRoom: [0, [Validators.min(-1)]],
      maxBedRoom: [0, [Validators.min(-1)]],
      minBedRoom: [0, [Validators.min(-1)]],
      maxParking: [0, [Validators.min(-1)]],
      minParking: [0, [Validators.min(-1)]],
      sendAgentsEmails: [true],
      buildings: this.formBuilder.array([], [Validators.maxLength(10)]),
    },
      { validators: comparePrice }
    );

    this.reactiveForm.get('introductionEN').valueChanges.pipe(
      debounceTime(this.debouncePeriod)
    ).subscribe((value) => {
      if (Date.now() - this.lastEditTime > this.debouncePeriod) {
        this.history.push(value);
      }
      this.lastEditTime = Date.now();
    });
  }

  get buildings() {
    return this.reactiveForm.get('buildings') as FormArray;
  }

  ngOnInit(): void {
    this.http.getAllTagsByUser().subscribe((res: any) => {
      console.log(res);
      this.tags = res.data;
      this.indoorFeatures = res.data.filter(feature => feature.type === 1);
      this.outdoorFeatures = res.data.filter(feature => feature.type === 2);
      this.energyFeatures = res.data.filter(feature => feature.type === 3);
      this.publicFeatures = res.data.filter(feature => feature.type === 4);
      this.cdr.detectChanges();
    });

    this.addBuilding({
      id: '',
      name: 'Main Tower',
    });
  }

  getAddress(address: any) {
    this.autocomplete.clear();
    const components = address.address_components;
    let addressLine2 = '';
    let area = '';
    let city = '';
    let state = '';
    let country = '';
    let postCode = '';
    components.forEach((component: { long_name: string; short_name: string; types: Array<string> }) => {
      if (component.types.find(type => type === 'street_number')) {
        addressLine2 = component.long_name;
      } else if (component.types.find(type => type === 'route')) {
        addressLine2 += ' ' + component.long_name;
      } else if (component.types.find(type => type === 'locality')) {
        area = component.long_name;
      } else if (component.types.find(type => type === 'administrative_area_level_2')) {
        city = component.short_name;
      } else if (component.types.find(type => type === 'administrative_area_level_1')) {
        state = component.short_name;
      } else if (component.types.find(type => type === 'country')) {
        country = component.long_name;
      } else if (component.types.find(type => type === 'postal_code')) {
        postCode = component.long_name;
      }
    });
    console.log(address);
    this.reactiveForm.get('addressLine1').setValue(addressLine2);
    this.reactiveForm.get('area').setValue(area);
    this.reactiveForm.get('city').setValue(city);
    this.reactiveForm.get('state').setValue(state);
    this.reactiveForm.get('country').setValue(country);
    this.reactiveForm.get('postCode').setValue(postCode);

    this.getCoordinatesFromAddress(this.reactiveForm.get('addressLine1').value + ' ' + this.reactiveForm.get('area').value + ' ' + this.reactiveForm.get('city').value + ' ' + this.reactiveForm.get('postCode').value)
      .subscribe((coordinate: any) => {
        console.log(coordinate);
        this.selectedLat = coordinate.lat;
        this.selectedLng = coordinate.lng;
        this.reactiveForm.patchValue({
          lat: coordinate.lat,
          lng: coordinate.lng
        });
        this.cdr.detectChanges();
      });
    this.cdr.detectChanges();
  }

  onSubmit() {
    this.dateValidation();
    this.dataMinValidation('minParking', 'maxParking');
    this.dataValidation('minTotalArea', 'maxTotalArea');
    this.dataMinValidation('minBathRoom', 'maxBathRoom');
    this.dataMinValidation('minBedRoom', 'maxBedRoom');
    this.ptypeShow = this.reactiveForm.get('ptype').value ? false : true;
    this.pstatusShow = this.reactiveForm.get('pstatus').value ? false : true;
    this.pnameShow = this.reactiveForm.get('pname').value ? false : true;
    this.isSubmitted = true;
    if (this.reactiveForm.invalid) {
      setTimeout(() => {
        const matdialogcontainer: any = document.getElementsByClassName('mat-dialog-container')[0];
        const container = document.getElementById('basic-info-container');
        const matError: any = container.getElementsByClassName('mat-error')[0];
        const offsetTop = matError.offsetTop - matdialogcontainer.offsetTop - 80;

        console.log(matError, matError.offsetTop, matdialogcontainer.offsetTop);
        matdialogcontainer.scrollTo(0, offsetTop);
      }, 500);
      return;
    }

    if (!this.reactiveForm.get('state').value) {
      return;
    }

    this.userService.currentUser.subscribe((user: any) => {
      this.companyId = user.company.id;
      const fd = new FormData();

      if (this.landProjectId) {
        fd.append('landProjectId', this.landProjectId);
      }

      fd.append('latitude', this.reactiveForm.get('lat').value);
      fd.append('longitude', this.reactiveForm.get('lng').value);
      fd.append('companyId', this.companyId);
      fd.append('projectTypeIds', this.reactiveForm.get('ptype').value.toString());
      fd.append('projectStatus', this.reactiveForm.get('pstatus').value);
      fd.append('name', this.reactiveForm.get('pname').value);
      fd.append('firb', this.reactiveForm.get('firb').value);
      fd.append('addressLine2', this.reactiveForm.get('addressLine1').value);
      fd.append('area', this.reactiveForm.get('area').value);
      fd.append('city', this.reactiveForm.get('city').value);
      fd.append('postCode', this.reactiveForm.get('postCode').value);
      fd.append('state', this.reactiveForm.get('state').value);
      fd.append('country', this.reactiveForm.get('country').value);

      if (this.reactiveForm.get('introductionEN').value || this.reactiveForm.get('introductionZH').value) {
        fd.append('notes', JSON.stringify({
          zh: this.reactiveForm.get('introductionZH').value,
          en: this.reactiveForm.get('introductionEN').value,
        }));
      }

      if (this.reactiveForm.get('commission').value) {
        console.log(this.reactiveForm.get('commission').value);
        fd.append('commissionFee', this.reactiveForm.get('commission').value);
      }

      if (this.reactiveForm.get('commissionUnit').value) {
        console.log(this.reactiveForm.get('commissionUnit').value);
        fd.append('commissionFeeUnit', this.reactiveForm.get('commissionUnit').value);
      }

      if (this.reactiveForm.get('website').value) {
        fd.append('website', this.reactiveForm.get('website').value);
      }

      if (this.reactiveForm.get('totalUnit').value) {
        fd.append('totalUnit', this.reactiveForm.get('totalUnit').value);
      }
      if (this.reactiveForm.get('level').value) {
        fd.append('level', this.reactiveForm.get('level').value);
      }
      if (this.startTimestamp) {
        fd.append('startTime', this.startTimestamp);
      }
      if (this.endTimestamp) {
        fd.append('endTime', this.endTimestamp);
      }
      if (this.sunsetTimestamp) {
        fd.append('sunsetTime', this.sunsetTimestamp);
      }

      if (this.selectedUserIds.length > 0) {
        fd.append('userIds', this.selectedUserIds.toString());
      }

      if (this.selectedFeatureIdArray.length > 0) {
        fd.append('tagIds', this.selectedFeatureIdArray.toString());
      }

      if (this.reactiveForm.get('cityCouncil').value) {
        fd.append('cityCouncil', this.reactiveForm.get('cityCouncil').value);
      }

      if (this.reactiveForm.get('maxPrice').value) {
        fd.append('maxPrice', this.reactiveForm.get('maxPrice').value);
      }

      if (this.reactiveForm.get('minPrice').value) {
        fd.append('minPrice', this.reactiveForm.get('minPrice').value);
      }
      if (this.reactiveForm.get('maxTotalArea').value) {
        fd.append('maxTotalArea', this.reactiveForm.get('maxTotalArea').value);
      }

      if (this.reactiveForm.get('minTotalArea').value) {
        fd.append('minTotalArea', this.reactiveForm.get('minTotalArea').value);
      }
      if (this.reactiveForm.get('maxBathRoom').value) {
        fd.append('maxBathRoom', this.reactiveForm.get('maxBathRoom').value);
      } else {
        fd.append('maxBathRoom', '0');
      }

      if (this.reactiveForm.get('minBathRoom').value || this.reactiveForm.get('minBathRoom').value === 0) {
        fd.append('minBathRoom', this.reactiveForm.get('minBathRoom').value);
      } else {
        fd.append('minBathRoom', '0');
      }
      if (this.reactiveForm.get('maxBedRoom').value) {
        fd.append('maxBedRoom', this.reactiveForm.get('maxBedRoom').value);
      } else {
        fd.append('maxBedRoom', '0');
      }

      if (this.reactiveForm.get('minBedRoom').value || this.reactiveForm.get('minBedRoom').value === 0) {
        fd.append('minBedRoom', this.reactiveForm.get('minBedRoom').value);
      } else {
        fd.append('minBedRoom', '0');
      }
      if (this.reactiveForm.get('maxParking').value) {
        fd.append('maxParking', this.reactiveForm.get('maxParking').value);
      } else {
        fd.append('maxParking', '0');
      }

      if (this.reactiveForm.get('minParking').value || this.reactiveForm.get('minParking').value === 0) {
        fd.append('minParking', this.reactiveForm.get('minParking').value);
      } else {
        fd.append('minParking', '0');
      }
      const length = this.incentive.length;
      for (let i = 0; i < length; i++) {
        const value = this.reactiveForm.get('incentive' + (i + 1)).value;
        if (value) {
          this.incentive[i] = value;
        }
      }

      const hasIncentive = this.incentive.filter((i) => {
        return i !== null;
      }).length > 0;
      if (hasIncentive) {
        fd.append('incentive', JSON.stringify(this.incentive));
      }
      this.isCreating = true;
      fd.append('sendAgentsEmails', this.reactiveForm.get('sendAgentsEmails').value);
      this.http.createProject(fd).subscribe((r: any) => {
        console.log(r);
        if (r.code === 1) {
          // if is land, do not create building
          if (this.reactiveForm.get('ptype').value === '4') {
            this.projectService.projects.push(r.data);
            this.toastr.success('Project creation successful.');
            this.isCreating = false;
            this.router.navigateByUrl('/projects');
            console.log('created land');
          } else {
            // 创建 building
            const buildings = this.reactiveForm.get('buildings').value;
            if (buildings.length > 0) {
              buildings.forEach((building) => {

                console.log('create building');
                const fd = new FormData();
                fd.append('name', building.name);
                fd.append('projectTypeIds', this.reactiveForm.get('ptype').value.toString());
                fd.append('projectStatus', this.reactiveForm.get('pstatus').value);
                fd.append('firb', this.reactiveForm.get('firb').value);
                fd.append('buildingProjectId', r.data.id);
                fd.append('latitude', this.reactiveForm.get('lat').value);
                fd.append('longitude', this.reactiveForm.get('lng').value);
                fd.append('addressLine1', r.data.addressLine1 || '');
                fd.append('addressLine2', r.data.addressLine2 || '');
                fd.append('area', r.data.area);
                fd.append('city', r.data.city);
                fd.append('country', r.data.country);
                fd.append('postCode', r.data.postCode);
                fd.append('state', r.data.state);

                fd.append('companyId', this.companyId);

                this.http.createProject(fd).subscribe((r: any) => {
                  console.log(r);
                  if (r.code === 0) {
                    this.toastr.error('Failed to create building.');
                  }
                });

              });
            }

            if (!this.isPackage) {
              this.projectService.projects.push(r.data);
              this.toastr.success('Project creation successful.');
              this.isCreating = false;
              this.router.navigateByUrl('/projects');
            } else {
              console.log('closeDialogEvent.emit');
              this.toastr.success('Package creation successful.');
              this.closeDialogEvent.emit({ code: 1, data: r.data });
            }

            console.log('created building');
          }

        } else {
          this.toastr.error('Project creation failed.');
          this.isCreating = false;
          this.cdr.detectChanges();
        }

        this.dialogRef.close();
        console.log('dialogRef.close()');
      });
    });
  }

  getStartDate(event) {
    this.startTimestamp = new Date(event.value).getTime().toString();
  }

  getEndDate(event) {
    this.endTimestamp = new Date(event.value).getTime().toString();
  }

  getSunsetDate(event) {
    this.sunsetTimestamp = new Date(event.value).getTime().toString();
    console.log(event.value);
  }

  isCheckBoxSelected(productId: number) {
    return this.selectedFeatureIdArray.includes(productId);
  }

  select(event: MatCheckboxChange, productId) {
    console.log(this.selectedFeatureIdArray);
    if (event.checked) {
      this.selectedFeatureIdArray.push(productId);
    } else {
      const index = this.selectedFeatureIdArray.indexOf(productId);
      this.selectedFeatureIdArray.splice(index, 1);
    }
    console.log(this.selectedFeatureIdArray);
  }

  public getCoordinatesFromAddress(address) {
    this.geoCoder = new google.maps.Geocoder();
    return new Observable((observer => {
      this.geoCoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          observer.next({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          });
        } else {
          observer.next({ lat: 0, lng: 0 });
        }
        observer.complete();
      });
    }));
  }

  dataValidation(min, max) {

    if (this.reactiveForm.get(min).value !== '' && this.reactiveForm.get(max).value !== '' && this.reactiveForm.get(min).value !== null && this.reactiveForm.get(max).value !== null && this.reactiveForm.get(max).value < this.reactiveForm.get(min).value) {
      this.reactiveForm.get(max).setErrors({ incorrect: true });
    } else {
      this.reactiveForm.get(max).setErrors(null);
    }
  }

  dataMinValidation(min, max) {
    if (this.reactiveForm.get(min).value !== 0 && this.reactiveForm.get(max).value !== 0 && this.reactiveForm.get(min).value !== null && this.reactiveForm.get(max).value !== null && this.reactiveForm.get(max).value < this.reactiveForm.get(min).value) {
      this.reactiveForm.get(max).setErrors({ incorrect: true });
    } else {
      this.reactiveForm.get(max).setErrors(null);
    }
  }

  dateValidation() {

    if (this.startTimestamp && this.endTimestamp && this.endTimestamp < this.startTimestamp) {
      this.reactiveForm.get('endDate').setErrors({ incorrect: true });
    } else {
      this.reactiveForm.get('endDate').setErrors(null);
    }
  }


  newBuilding(building: any): FormGroup {
    if (building) {
      return this.formBuilder.group({
        id: building.id,
        name: building.name,
      });
    }
    return this.formBuilder.group({
      id: '',
      name: '',
    });
  }

  addBuilding(building?: any) {
    if (this.buildings.length >= 10) {
      this.toastr.warning('You can only add 10 buildings.');
      return;
    }
    this.buildings.push(this.newBuilding(building));
  }

  removeBuilding(i: number) {
    this.buildings.removeAt(i);
  }

  handleClearIndoor() {
    this.indoorFeatures.forEach((item) => {
      this.selectedFeatureIdArray.forEach((item1, i) => {
        if (item.id == item1) {
          this.selectedFeatureIdArray.splice(i, 1);
        }
      });
    });
  }

  handleClearPublic() {
    this.publicFeatures.forEach((item) => {
      this.selectedFeatureIdArray.forEach((item1, i) => {
        if (item.id == item1) {
          this.selectedFeatureIdArray.splice(i, 1);
        }
      });
    });
  }

  /**
   * @author Damon Gao
   * @description Undo content
   */
  undo() {
    if (this.history.length > 1) {
      this.history.pop();
      const lastValue = this.history[this.history.length - 1];
      this.reactiveForm.get('introductionEN').setValue(lastValue);
    } else {
      this.reactiveForm.get('introductionEN').setValue('');
    }
  }

  /**
   * @author Damon Gao
   * @description Generate AI description
   */
  generateAIDescription() {
    this.isGenerating = true;
    const projectStatus = this.getProjectStatus();
    const projectType = this.getProjectType();
    const data = this.collectProjectData(projectType, projectStatus);
    if (this.hasEmptyRequiredFields(data)) {
      this.toastr.error('Please fill in the required fields.');
      this.isGenerating = false;
      return;
    }
    this.populateOptionalFields(data);
    this.createProjectDescription(data);
  }

  getProjectStatus() {
    const statusMap = {
      1: 'Off market',
      2: 'Off the plan',
      3: 'Under construction',
      4: 'Complete',
    };
    return statusMap[this.reactiveForm.get('pstatus').value];
  }

  getProjectType() {
    const typeMap = {
      1: 'House',
      2: 'Townhouse',
      3: 'Apartment',
      4: 'House & Land',
    };
    return typeMap[this.reactiveForm.get('ptype').value];
  }

  collectProjectData(projectType: string, projectStatus: string): ProjectDescription {
    return {
      project_name: this.reactiveForm.get('pname').value,
      address: this.composeAddress(),
      project_type: projectType,
      project_status: projectStatus,
    };
  }

  composeAddress() {
    return [
      this.reactiveForm.get('addressLine1').value,
      this.reactiveForm.get('area').value,
      this.reactiveForm.get('city').value,
      this.reactiveForm.get('postCode').value,
    ].join(' ');
  }

  hasEmptyRequiredFields(data: ProjectDescription) {
    return Object.values(data).some(value => value === null || value === ''
      || value === undefined);
  }

  populateOptionalFields(data: ProjectDescription) {
    const optionalFields = [
      'totalUnit', 'minPrice', 'maxPrice', 'minBedRoom', 'maxBedRoom', 'minBathRoom', 'maxBathRoom', 'minParking',
      'maxParking',
    ];
    optionalFields.forEach((field) => {
      if (this.reactiveForm.get(field).value) {
        data[field] = this.reactiveForm.get(field).value;
      }
    });
    if (this.selectedFeatureIdArray.length > 0) {
      data.project_features = this.selectedFeatureIdArray;
    }
  }

  createProjectDescription(data: ProjectDescription) {
    this.http.createProjectDescription(data)
      .pipe(finalize(() => {
        this.isGenerating = false;
      }))
      .subscribe((res: any) => {
        console.log('---ai project generation---', res);
        this.reactiveForm.get('introductionEN').setValue(res.project_description);
      });
  }

  onSaveDraft() {
    this.dateValidation();
    this.dataMinValidation('minParking', 'maxParking');
    this.dataValidation('minTotalArea', 'maxTotalArea');
    this.dataMinValidation('minBathRoom', 'maxBathRoom');
    this.dataMinValidation('minBedRoom', 'maxBedRoom');
    this.reactiveForm.patchValue({
      pstatus: '5',
    });
    this.ptypeShow = !this.reactiveForm.get('ptype').value;
    // this.pstatusShow = !this.reactiveForm.get('pstatus').value;
    this.pnameShow = !this.reactiveForm.get('pname').value;
    const firbValue = this.reactiveForm.get('firb').value;
    if (firbValue === '' || firbValue === undefined) {
      this.reactiveForm.get('firb').setValue('1');
    }
    this.isSubmitted = true;
    if (this.reactiveForm.invalid) {
      setTimeout(() => {
        const matdialogcontainer: any = document.getElementsByClassName('mat-dialog-container')[0];
        const container = document.getElementById('basic-info-container');
        const matError: any = container.getElementsByClassName('mat-error')[0];
        const offsetTop = matError.offsetTop - matdialogcontainer.offsetTop - 80;

        console.log(matError, matError.offsetTop, matdialogcontainer.offsetTop);
        matdialogcontainer.scrollTo(0, offsetTop);
      }, 500);
      return;
    }

    if (!this.reactiveForm.get('state').value) {
      return;
    }

    this.userService.currentUser.subscribe((user: any) => {
      this.companyId = user.company.id;
      const fd = new FormData();

      if (this.landProjectId) {
        fd.append('landProjectId', this.landProjectId);
      }

      fd.append('latitude', this.reactiveForm.get('lat').value);
      fd.append('longitude', this.reactiveForm.get('lng').value);
      fd.append('companyId', this.companyId);
      fd.append('projectTypeIds', this.reactiveForm.get('ptype').value.toString());
      fd.append('projectStatus', '5');
      fd.append('name', this.reactiveForm.get('pname').value);
      fd.append('firb', this.reactiveForm.get('firb').value ? this.reactiveForm.get('firb').value : '1');
      fd.append('addressLine2', this.reactiveForm.get('addressLine1').value);
      fd.append('area', this.reactiveForm.get('area').value);
      fd.append('city', this.reactiveForm.get('city').value);
      fd.append('postCode', this.reactiveForm.get('postCode').value);
      fd.append('state', this.reactiveForm.get('state').value);
      fd.append('country', this.reactiveForm.get('country').value);
      fd.append('businessType', '1');
      if (this.reactiveForm.get('introductionEN').value || this.reactiveForm.get('introductionZH').value) {
        fd.append('notes', JSON.stringify({
          zh: this.reactiveForm.get('introductionZH').value,
          en: this.reactiveForm.get('introductionEN').value,
        }));
      }

      if (this.reactiveForm.get('commission').value) {
        console.log(this.reactiveForm.get('commission').value);
        fd.append('commissionFee', this.reactiveForm.get('commission').value);
      }

      if (this.reactiveForm.get('commissionUnit').value) {
        console.log(this.reactiveForm.get('commissionUnit').value);
        fd.append('commissionFeeUnit', this.reactiveForm.get('commissionUnit').value);
      }

      if (this.reactiveForm.get('website').value) {
        fd.append('website', this.reactiveForm.get('website').value);
      }

      if (this.reactiveForm.get('totalUnit').value) {
        fd.append('totalUnit', this.reactiveForm.get('totalUnit').value);
      }
      if (this.reactiveForm.get('level').value) {
        fd.append('level', this.reactiveForm.get('level').value);
      }
      if (this.startTimestamp) {
        fd.append('startTime', this.startTimestamp);
      }
      if (this.endTimestamp) {
        fd.append('endTime', this.endTimestamp);
      }
      if (this.sunsetTimestamp) {
        fd.append('sunsetTime', this.sunsetTimestamp);
      }

      if (this.selectedUserIds.length > 0) {
        fd.append('userIds', this.selectedUserIds.toString());
      }

      if (this.selectedFeatureIdArray.length > 0) {
        fd.append('tagIds', this.selectedFeatureIdArray.toString());
      }

      if (this.reactiveForm.get('cityCouncil').value) {
        fd.append('cityCouncil', this.reactiveForm.get('cityCouncil').value);
      }

      if (this.reactiveForm.get('maxPrice').value) {
        fd.append('maxPrice', this.reactiveForm.get('maxPrice').value);
      }

      if (this.reactiveForm.get('minPrice').value) {
        fd.append('minPrice', this.reactiveForm.get('minPrice').value);
      }
      if (this.reactiveForm.get('maxTotalArea').value) {
        fd.append('maxTotalArea', this.reactiveForm.get('maxTotalArea').value);
      }

      if (this.reactiveForm.get('minTotalArea').value) {
        fd.append('minTotalArea', this.reactiveForm.get('minTotalArea').value);
      }
      if (this.reactiveForm.get('maxBathRoom').value) {
        fd.append('maxBathRoom', this.reactiveForm.get('maxBathRoom').value);
      } else {
        fd.append('maxBathRoom', '0');
      }

      if (this.reactiveForm.get('minBathRoom').value || this.reactiveForm.get('minBathRoom').value === 0) {
        fd.append('minBathRoom', this.reactiveForm.get('minBathRoom').value);
      } else {
        fd.append('minBathRoom', '0');
      }
      if (this.reactiveForm.get('maxBedRoom').value) {
        fd.append('maxBedRoom', this.reactiveForm.get('maxBedRoom').value);
      } else {
        fd.append('maxBedRoom', '0');
      }

      if (this.reactiveForm.get('minBedRoom').value || this.reactiveForm.get('minBedRoom').value === 0) {
        fd.append('minBedRoom', this.reactiveForm.get('minBedRoom').value);
      } else {
        fd.append('minBedRoom', '0');
      }
      if (this.reactiveForm.get('maxParking').value) {
        fd.append('maxParking', this.reactiveForm.get('maxParking').value);
      } else {
        fd.append('maxParking', '0');
      }

      if (this.reactiveForm.get('minParking').value || this.reactiveForm.get('minParking').value === 0) {
        fd.append('minParking', this.reactiveForm.get('minParking').value);
      } else {
        fd.append('minParking', '0');
      }
      const length = this.incentive.length;
      for (let i = 0; i < length; i++) {
        const value = this.reactiveForm.get('incentive' + (i + 1)).value;
        if (value) {
          this.incentive[i] = value;
        }
      }

      const hasIncentive = this.incentive.filter((i) => {
        return i !== null;
      }).length > 0;
      if (hasIncentive) {
        fd.append('incentive', JSON.stringify(this.incentive));
      }
      this.http.saveDraftProject(fd).subscribe((r: any) => {
        if (r.code === 1) {
          console.log('-----draft----', r);
          this.dialogRef.close();
          this.toastr.success('Project draft saved.');
        }
      });
    });
  }
}
